import { z } from "zod";

/**
 * Base Authentication schema matching the API response
 */
export const AuthenticationSchema = z.object({
  id: z.number().describe("Unique identifier for the authentication"),
  date: z.string().describe("Date created"),
  lastchanged: z.string().optional().describe("Date last changed"),
  account_id: z
    .number()
    .describe("Account ID associated with this authentication"),
  customuser_id: z
    .number()
    .optional()
    .describe("Custom user ID (if applicable)"),
  selected_api: z.string().describe("Selected API key (internal identifier)"),
  destination_selected_api: z
    .string()
    .nullable()
    .optional()
    .describe("Destination API key (if applicable)"),
  is_invite_only: z
    .boolean()
    .describe("Whether the authentication is invite-only"),
  is_private: z.boolean().describe("Whether the authentication is private"),
  shared_with_all: z
    .boolean()
    .describe("Whether the authentication is shared with all users"),
  is_stale: z.string().optional().describe("Stale status string"),
  is_shared: z.string().optional().describe("Shared status string"),
  marked_stale_at: z
    .string()
    .nullable()
    .optional()
    .describe("Date when marked stale"),
  label: z
    .string()
    .nullable()
    .optional()
    .describe("User label for the authentication"),
  identifier: z.string().nullable().optional().describe("Identifier"),
  title: z
    .string()
    .nullable()
    .optional()
    .describe("Title of the authentication"),
  url: z.string().optional().describe("URL to the authentication resource"),
  groups: z
    .array(
      z
        .record(z.string(), z.unknown())
        .describe("Groups associated with the authentication"),
    )
    .optional()
    .describe("Array of groups associated with the authentication"),
  members: z
    .string()
    .optional()
    .describe("Members associated with the authentication"),
  permissions: z
    .record(z.string(), z.boolean())
    .optional()
    .describe("Permissions for the authentication"),
  public_id: z
    .string()
    .optional()
    .describe("Public UUID for the authentication"),
  account_public_id: z
    .string()
    .optional()
    .describe("Public UUID for the associated account"),
  customuser_public_id: z
    .string()
    .optional()
    .describe("Public UUID for the associated custom user"),
});

/**
 * Normalized authentication item returned by getAuthentication handler
 *
 * Transforms API response fields:
 * - selected_api → implementation_id
 * - customuser_id → profile_id
 * - is_stale → is_expired (preserved as is_stale too)
 * - marked_stale_at → expired_at (preserved as marked_stale_at too)
 *
 * Adds computed fields:
 * - app_key: Extracted from selected_api (e.g., "SlackCLIAPI@1.0.0" → "SlackCLIAPI")
 * - app_version: Extracted from selected_api (e.g., "SlackCLIAPI@1.0.0" → "1.0.0")
 */
export const AuthenticationItemSchema = AuthenticationSchema.omit({
  selected_api: true,
  customuser_id: true,
}).extend({
  // Override numeric IDs with string versions (converted by normalizeAuthenticationItem)
  id: z.string().describe("Unique identifier for the authentication"),
  account_id: z
    .string()
    .describe("Account ID associated with this authentication"),

  // Renamed fields
  implementation_id: z
    .string()
    .optional()
    .describe("Implementation ID (was selected_api)"),
  profile_id: z.string().optional().describe("Profile ID (was customuser_id)"),

  // Mapped fields (originals preserved in ...restOfAuth)
  is_expired: z
    .string()
    .optional()
    .describe("Whether the authentication is expired (mapped from is_stale)"),
  expired_at: z
    .string()
    .nullable()
    .optional()
    .describe("Date when authentication expired (mapped from marked_stale_at)"),

  // Computed fields
  app_key: z
    .string()
    .optional()
    .describe("App Key extracted from implementation_id"),
  app_version: z
    .string()
    .optional()
    .describe("App Version extracted from implementation_id"),
});

export const AuthenticationsResponseSchema = z.object({
  count: z.number().describe("Total number of items"),
  next: z
    .string()
    .nullable()
    .optional()
    .describe("Cursor for the next page of results (if available)"),
  previous: z
    .string()
    .nullable()
    .optional()
    .describe("Cursor for the previous page of results (if available)"),
  results: z
    .array(AuthenticationSchema)
    .describe("Array of authentication items"),
});

export type Authentication = z.infer<typeof AuthenticationSchema>;
export type AuthenticationItem = z.infer<typeof AuthenticationItemSchema>;
export type AuthenticationsResponse = z.infer<
  typeof AuthenticationsResponseSchema
>;

/**
 * Path parameters schema for getAuthentication endpoint.
 */
export const GetAuthenticationParamSchema = z
  .object({
    authenticationId: z.string().describe("Authentication ID to retrieve"),
  })
  .describe("Get a specific authentication by ID");

export type GetAuthenticationParam = z.infer<
  typeof GetAuthenticationParamSchema
>;

/**
 * Response type for getAuthentication endpoint.
 * Wraps AuthenticationItem in the standard API response envelope.
 */
export type GetAuthenticationResponse = { data: AuthenticationItem };

/**
 * Query parameters for listing authentications
 */
export const ListAuthenticationsQuerySchema = z
  .object({
    /** @deprecated Use app_key instead */
    appKey: z
      .string()
      .optional()
      .describe(
        "Deprecated: Use app_key instead. Filter authentications by app key (e.g., 'SlackCLIAPI' or slug like 'github')",
      ),
    app_key: z
      .string()
      .optional()
      .describe(
        "Filter authentications by app key (e.g., 'SlackCLIAPI' or slug like 'github')",
      ),
    /** @deprecated Use authentication_ids instead */
    authenticationIds: z
      .string()
      .optional()
      .describe(
        "Deprecated: Use authentication_ids instead. Comma-separated list of authentication IDs to filter by",
      ),
    authentication_ids: z
      .string()
      .optional()
      .describe("Comma-separated list of authentication IDs to filter by"),
    search: z
      .string()
      .optional()
      .describe("Search term to filter authentications by title"),
    title: z
      .string()
      .optional()
      .describe(
        "Filter authentications by exact title match (searches first, then filters locally)",
      ),
    /** @deprecated Use account_id instead */
    accountId: z
      .string()
      .optional()
      .describe(
        "Deprecated: Use account_id instead. Filter authentications by account ID",
      ),
    account_id: z
      .string()
      .optional()
      .describe("Filter authentications by account ID"),
    owner: z
      .string()
      .optional()
      .describe(
        "Filter by owner, 'me' for your own authentications or a specific user ID",
      ),
    /** @deprecated Use is_expired instead */
    isExpired: z
      .boolean()
      .optional()
      .describe(
        "Deprecated: Use is_expired instead. Filter by expired status (true = expired only, false = non-expired only)",
      ),
    is_expired: z
      .boolean()
      .optional()
      .describe(
        "Filter by expired status (true = expired only, false = non-expired only)",
      ),
    /** @deprecated Use page_size instead */
    pageSize: z
      .number()
      .min(1)
      .optional()
      .describe(
        "Deprecated: Use page_size instead. Number of authentications per page",
      ),
    page_size: z
      .number()
      .min(1)
      .optional()
      .describe("Number of authentications per page"),
    offset: z
      .string()
      .optional()
      .describe("Pagination offset from previous response"),
  })
  .describe("Query parameters for listing authentications");

/**
 * Response schema for listAuthentications
 */
export const ListAuthenticationsResponseSchema = z
  .object({
    data: z
      .array(AuthenticationItemSchema)
      .describe("Array of authentication items"),
    links: z
      .object({
        next: z
          .string()
          .nullable()
          .optional()
          .describe(
            "Fully qualified URL for the next page of results (if available), e.g. https://sdkapi.zapier.com/api/v0/authentications?offset=100&pageSize=50",
          ),
      })
      .describe("Pagination links for navigating through results"),
    meta: z
      .object({
        count: z.number().describe("Total number of items"),
        limit: z.number().describe("Number of items per page"),
        offset: z.number().describe("Offset of the current page"),
      })
      .describe("Metadata for the paginated result set"),
  })
  .describe("Response schema for listing authentications");

/**
 * TypeScript types for request and response
 */
export type ListAuthenticationsQuery = z.infer<
  typeof ListAuthenticationsQuerySchema
>;
export type ListAuthenticationsResponse = z.infer<
  typeof ListAuthenticationsResponseSchema
>;
